import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Lock, CheckCircle, Clock, FileText, ClipboardCheck } from 'lucide-react';
import { lmsApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [course, setCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isStudent = user?.role === 'student';

  useEffect(() => {
    loadCourse();
  }, [id]);

  const loadCourse = async () => {
    try {
      const res = await lmsApi.getCourse(id);
      setCourse(res.data);
    } catch (error) {
      if (error.response?.data?.code === 'LEVEL_REQUIRED') {
        toast.error('Upgrade your plan to access this course');
      } else {
        toast.error('Failed to load course');
      }
      navigate('/lms/courses');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!course) return null;

  const completedCount = course.lessons?.filter(l => l.progress_status === 'completed').length || 0;
  const totalLessons = course.lessons?.length || 0;
  const progressPercent = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/lms/courses')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Courses
      </button>

      {/* Course Header */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center space-x-2 mb-2">
          {course.levels?.map(level => (
            <span
              key={level}
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                level === 'L1' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
              }`}
            >
              {level}
            </span>
          ))}
        </div>
        <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>
        {course.description && (
          <p className="text-gray-600 mt-2">{course.description}</p>
        )}
        <div className="flex items-center space-x-4 mt-4 text-sm text-gray-500">
          {course.creator_name && <span>By {course.creator_name}</span>}
          <span>{totalLessons} lesson{totalLessons !== 1 ? 's' : ''}</span>
        </div>

        {isStudent && totalLessons > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
              <span>{completedCount} of {totalLessons} completed</span>
              <span>{progressPercent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-primary-600 h-2.5 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Lessons List */}
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Lessons</h2>
      {course.lessons?.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
          <p>No lessons available yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {course.lessons?.map((lesson, index) => {
            const isCompleted = lesson.progress_status === 'completed';
            const isInProgress = lesson.progress_status === 'in_progress';
            const hasAccess = lesson.hasAccess;

            return (
              <div
                key={lesson.id}
                onClick={() => hasAccess ? navigate(`/lms/lessons/${lesson.id}`) : null}
                className={`
                  flex items-center p-4 bg-white border rounded-lg transition-all
                  ${hasAccess ? 'border-gray-200 cursor-pointer hover:border-primary-300 hover:shadow-sm' : 'border-gray-100 opacity-60 cursor-not-allowed'}
                `}
              >
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0
                  ${isCompleted ? 'bg-green-100' : isInProgress ? 'bg-primary-100' : 'bg-gray-100'}
                `}>
                  {!hasAccess ? (
                    <Lock className="w-4 h-4 text-gray-400" />
                  ) : isCompleted ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <span className="text-sm font-medium text-gray-500">{index + 1}</span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate">{lesson.title}</h3>
                  {lesson.description && (
                    <p className="text-sm text-gray-500 truncate mt-0.5">{lesson.description}</p>
                  )}
                </div>

                <div className="flex items-center space-x-3 ml-4 flex-shrink-0">
                  {lesson.levels?.map(level => (
                    <span
                      key={level}
                      className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        level === 'L1' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'
                      }`}
                    >
                      {level}
                    </span>
                  ))}
                  {lesson.duration_seconds > 0 && (
                    <span className="text-xs text-gray-400 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {Math.ceil(lesson.duration_seconds / 60)}m
                    </span>
                  )}
                  {lesson.assessment_id && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 font-medium flex items-center">
                      <ClipboardCheck className="w-3 h-3 mr-0.5" />
                      Quiz
                    </span>
                  )}
                  {hasAccess && lesson.youtube_video_id && (
                    <Play className="w-4 h-4 text-primary-500" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CourseDetail;
